import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material';

/**
 * The house button look, for places that style a plain MUI Button or Box
 * instead of using DosenButton. Rules:
 *  - Buttons carry text only, no icons (icon-only buttons excepted).
 *  - Primary: orange gradient with white text.
 *  - Secondary: dark fill, white text, hairline border.
 *  - Disabled: dark with dim text (never a faded orange), and it lights up
 *    once the action is available.
 *  - Working: the label changes ("Saving...") instead of showing a spinner.
 */

export const CTA_GRADIENT = 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)';
export const CTA_GRADIENT_HOVER = 'linear-gradient(180deg, #FFA135 0%, #F46A32 100%)';

// Kept in step with the --*-button-cta-disabled tokens in theme/tokens.css.
export const buttonDisabledSx = {
  background: 'rgba(0, 0, 0, 0.2)',
  color: '#565656',
  boxShadow: 'inset 0 0 0 1px #2A2A2A',
  opacity: 1
};

// Plain style objects (not SxProps) so callers can spread them into their own sx.
export const ctaButtonSx: SystemStyleObject<Theme> = {
  background: CTA_GRADIENT,
  color: '#FDFDFD',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.5,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '10px 14px',
  boxShadow: 'none',
  '&:hover': { background: CTA_GRADIENT_HOVER, boxShadow: 'none' },
  '&.Mui-disabled, &:disabled, &[aria-disabled="true"]': buttonDisabledSx
};

export const secondaryButtonSx: SystemStyleObject<Theme> = {
  background: '#1C1C1C',
  color: '#FDFDFD',
  border: '1px solid #2A2A2A',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.5,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '9px 14px',
  boxShadow: 'none',
  '&:hover': { background: '#262626', boxShadow: 'none' },
  '&.Mui-disabled, &:disabled, &[aria-disabled="true"]': {
    background: 'rgba(0, 0, 0, 0.2)',
    color: '#565656',
    borderColor: '#2A2A2A',
    opacity: 1
  }
};
